/**
 * Host vehicles hook — TanStack Query for host's vehicle listings + CRUD mutations.
 *
 * Provides queries for the host's vehicle list, makes/models lookups,
 * and mutations for create, update, delete, and status changes.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { vehicleService } from '../services/vehicle.service'
import { useAuth } from './use-auth'
import type { CreateVehicleInput, UpdateVehicleInput } from '../types/vehicle.types'

// ─── Query Keys ─────────────────────────────────────────────────────────────

const hostVehicleKeys = {
  all: ['host-vehicles'] as const,
  list: (hostId: string, organizationId?: string | null) =>
    [...hostVehicleKeys.all, 'list', hostId, organizationId ?? 'personal'] as const,
  detail: (vehicleId: string) => [...hostVehicleKeys.all, 'detail', vehicleId] as const,
  makes: ['vehicle-makes'] as const,
  models: (makeId: string) => ['vehicle-models', makeId] as const,
  // Bajo `all`, así toda mutación que ya invalida `hostVehicleKeys.all` también
  // refresca los blockers: subir un documento o publicar cambia la respuesta.
  blockers: (hostId: string) => [...hostVehicleKeys.all, 'blockers', hostId] as const,
}

// ─── Listing blockers (los decide el servidor) ──────────────────────────────

/**
 * Blockers de publicación por vehículo, servidos por el gate canónico.
 *
 * La app dejó de re-implementar la regla: `list_my_vehicle_listing_blockers`
 * devuelve los mismos códigos que la compuerta de activación, la reserva y la
 * notificación. Acá solo se indexan por vehículo para pintarlos con i18n.
 */
export function useListingBlockers() {
  const { user } = useAuth()
  const hostId = user?.id ?? ''

  return useQuery({
    queryKey: hostVehicleKeys.blockers(hostId),
    queryFn: () => vehicleService.getMyListingBlockers(),
    enabled: !!hostId,
    staleTime: 60 * 1000,
    select: (rows) => new Map(rows.map((row) => [row.vehicleId, row.blockers])),
  })
}

// ─── Host Vehicles List + CRUD ──────────────────────────────────────────────

export function useHostVehicles(organizationId?: string | null) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const hostId = user?.id ?? ''
  const listKey = hostVehicleKeys.list(hostId, organizationId)

  // ── List query ────────────────────────────────────────────────────────

  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: () => vehicleService.getHostVehicles(hostId, organizationId),
    enabled: !!hostId,
    staleTime: 2 * 60 * 1000, // 2 min
  })

  // ── Create mutation ───────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (input: CreateVehicleInput) => vehicleService.createVehicle(hostId, input),
    onSuccess: (vehicle) => {
      void queryClient.invalidateQueries({ queryKey: hostVehicleKeys.all })
      queryClient.setQueryData(hostVehicleKeys.detail(vehicle.id), vehicle)
    },
  })

  // ── Update mutation ───────────────────────────────────────────────────

  const updateMutation = useMutation({
    mutationFn: ({ vehicleId, input }: { vehicleId: string; input: UpdateVehicleInput }) =>
      vehicleService.updateVehicle(vehicleId, input),
    onSuccess: (vehicle) => {
      void queryClient.invalidateQueries({ queryKey: hostVehicleKeys.all })
      queryClient.setQueryData(hostVehicleKeys.detail(vehicle.id), vehicle)
    },
  })

  // ── Delete mutation ───────────────────────────────────────────────────

  const deleteMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.deleteVehicle(vehicleId),
    onSuccess: (_data, vehicleId) => {
      void queryClient.invalidateQueries({ queryKey: hostVehicleKeys.all })
      queryClient.removeQueries({ queryKey: hostVehicleKeys.detail(vehicleId) })
    },
  })

  // ── Status mutation ───────────────────────────────────────────────────

  const statusMutation = useMutation({
    mutationFn: ({
      vehicleId,
      status,
    }: {
      vehicleId: string
      status: 'draft' | 'pending_review' | 'active' | 'paused'
    }) => vehicleService.setVehicleStatus(vehicleId, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostVehicleKeys.all })
    },
  })

  // ── Publish (submit for review) ───────────────────────────────────────

  const publishMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.publishVehicle(vehicleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostVehicleKeys.all })
    },
  })

  return {
    // List
    vehicles: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    // Without this a failed fetch is indistinguishable from an empty list,
    // because `data ?? []` collapses both into the same value — the screens
    // then told the host "you have no vehicles" when the request had simply
    // failed, with no way to retry.
    isError: listQuery.isError,
    isRefetching: listQuery.isRefetching,
    refetch: listQuery.refetch,

    // Computed
    activeCount: (listQuery.data ?? []).filter((v) => v.status === 'active').length,
    draftCount: (listQuery.data ?? []).filter((v) => v.status === 'draft').length,

    // Mutations
    createVehicle: createMutation.mutateAsync,
    updateVehicle: updateMutation.mutateAsync,
    deleteVehicle: deleteMutation.mutateAsync,
    setStatus: statusMutation.mutateAsync,
    publish: publishMutation.mutateAsync,

    // Pending states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isPublishing: publishMutation.isPending,
  }
}

// ─── Makes & Models Lookups ─────────────────────────────────────────────────

export function useMakes() {
  return useQuery({
    queryKey: hostVehicleKeys.makes,
    queryFn: () => vehicleService.getMakes(),
    staleTime: 30 * 60 * 1000, // 30 min — rarely changes
  })
}

export function useModels(makeId: string | undefined) {
  return useQuery({
    queryKey: hostVehicleKeys.models(makeId ?? ''),
    queryFn: () => vehicleService.getModels(makeId!),
    enabled: !!makeId,
    staleTime: 30 * 60 * 1000,
  })
}
